import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import { GENERATED_PAGES } from '../generated-pages';

@Component({
  selector: 'app-page',
  standalone: false,
  templateUrl: './page.component.html'
})
export class PageComponent implements OnInit, AfterViewInit, OnDestroy {
  content: SafeHtml | null = null;
  formMessage = '';
  private destroy$ = new Subject<void>();

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly sanitizer: DomSanitizer,
    private readonly elementRef: ElementRef<HTMLElement>
  ) {}

  ngOnInit(): void {
    this.route.data
      .pipe(takeUntil(this.destroy$))
      .subscribe(data => {
        const page = String(data.page || 'home');
        this.loadPage(page);
      });

    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(() => this.scrollToFragment());
  }

  ngAfterViewInit(): void {
    this.scrollToFragment();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onContentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement | null;
    const anchor = target?.closest('a') as HTMLAnchorElement | null;
    if (!anchor) {
      return;
    }

    const href = anchor.getAttribute('href');
    if (!href || href.startsWith('mailto:') || href.startsWith('tel:') || href.startsWith('http')) {
      return;
    }

    event.preventDefault();
    this.formMessage = '';
    void this.router.navigateByUrl(href);
  }

  onContentSubmit(event: Event): void {
    const form = event.target as HTMLFormElement | null;
    if (!form || !form.matches('form')) {
      return;
    }

    event.preventDefault();
    const data = new FormData(form);
    const email = String(data.get('email') || '').trim();
    const name = String(data.get('name') || '').trim();
    const subject = String(data.get('subject') || 'Website enquiry').trim();
    const message = String(data.get('message') || '').trim();

    if (!email) {
      this.formMessage = 'Please enter your email address.';
      return;
    }

    const body = [
      name ? `Name: ${name}` : '',
      `Email: ${email}`,
      message ? `Message: ${message}` : ''
    ].filter(Boolean).join('\n\n');

    window.location.href = `mailto:info@anikinitek.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    this.formMessage = 'Your email app has been opened to send the request.';
    form.reset();
  }

  private loadPage(page: string): void {
    this.content = null;
    this.formMessage = '';
    const html = GENERATED_PAGES[page] || '<main id="main"><section class="inner-page"><div class="container"><p>Page content could not be loaded.</p></div></section></main>';
    this.content = this.sanitizer.bypassSecurityTrustHtml(html);
    window.setTimeout(() => this.scrollToFragment(), 0);
  }

  private scrollToFragment(): void {
    const fragment = this.route.snapshot.fragment;
    if (!fragment) {
      window.scrollTo({ top: 0 });
      return;
    }

    window.setTimeout(() => {
      const host = this.elementRef.nativeElement;
      const target = host.querySelector(`#${CSS.escape(fragment)}`) as HTMLElement | null;
      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 50);
  }
}
